import React from 'react';
import {
  Activity,
  BarChart2,
  BookOpen,
  Cpu,
  LayoutGrid,
  Play,
  Search,
} from 'lucide-react';

interface SidebarProps {
  activeTab: 'macro' | 'scanner' | 'sectors' | 'studio' | 'guide';
  setActiveTab: (tab: 'macro' | 'scanner' | 'sectors' | 'studio' | 'guide') => void;
  onOpenDrawer?: () => void;
  onResetToLanding?: () => void;
  onRunBacktest?: () => void;
  onOpenCheatSheet?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  setActiveTab,
  onOpenDrawer,
  onResetToLanding,
  onRunBacktest,
  onOpenCheatSheet,
}) => {
  return (
    <aside className="hidden lg:flex flex-col justify-between py-3.5 border-r border-[#182232] bg-[#0b0f17] w-52 shrink-0 select-none sticky top-11 h-[calc(100vh-2.75rem)] overflow-y-auto z-30">
      <div className="flex flex-col gap-1.5">
        {/* Institutional Desk Identity */}
        <div
          onClick={onResetToLanding ? onResetToLanding : () => setActiveTab('macro')}
          className="px-3.5 pb-2.5 cursor-pointer hover:opacity-85 transition-opacity"
          title="返回宏觀首頁 (Return to Landing Page)"
        >
          <div className="text-sm font-bold text-slate-100 tracking-tight">TWSE/TPEx Pro</div>
          <div className="text-[11px] font-mono font-medium text-sky-400/85 mt-0.5">
            Kostolany Cycle Engine v1.5
          </div>
        </div>

        {/* 5 Primary Navigation Nodes */}
        <nav className="flex flex-col gap-1 px-2">
          {/* Tab 1: 宏觀週期 */}
          <button
            type="button"
            data-testid="sidebar-macro-btn"
            onClick={() => setActiveTab('macro')}
            className={`w-full px-3 py-2 rounded-md flex items-center gap-2.5 text-[13px] font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'macro'
                ? 'bg-blue-950/60 text-terminal-cyan border-l-2 border-primary-container font-semibold shadow-sm'
                : 'text-slate-300 hover:text-white hover:bg-[#141923]'
            }`}
          >
            <Activity className={`w-4 h-4 ${activeTab === 'macro' ? 'text-terminal-cyan' : 'text-sky-400/70'}`} />
            <span>宏觀週期</span>
          </button>

          {/* Tab 2: 個股掃描 */}
          <button
            type="button"
            data-testid="sidebar-scanner-btn"
            onClick={() => setActiveTab('scanner')}
            className={`w-full px-3 py-2 rounded-md flex items-center gap-2.5 text-[13px] font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'scanner'
                ? 'bg-blue-950/60 text-terminal-cyan border-l-2 border-primary-container font-semibold shadow-sm'
                : 'text-slate-300 hover:text-white hover:bg-[#141923]'
            }`}
          >
            <Search className={`w-4 h-4 ${activeTab === 'scanner' ? 'text-terminal-cyan' : 'text-sky-400/70'}`} />
            <span>個股掃描</span>
          </button>

          {/* Tab 3: 類股板塊 */}
          <button
            type="button"
            data-testid="sidebar-sectors-btn"
            onClick={() => setActiveTab('sectors')}
            className={`w-full px-3 py-2 rounded-md flex items-center gap-2.5 text-[13px] font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'sectors'
                ? 'bg-blue-950/60 text-terminal-cyan border-l-2 border-primary-container font-semibold shadow-sm'
                : 'text-slate-300 hover:text-white hover:bg-[#141923]'
            }`}
          >
            <LayoutGrid className={`w-4 h-4 ${activeTab === 'sectors' ? 'text-terminal-cyan' : 'text-sky-400/70'}`} />
            <span>類股板塊熱力</span>
          </button>

          {/* Tab 4: 深度診斷 */}
          <button
            type="button"
            data-testid="sidebar-diagnosis-btn"
            onClick={() => {
              if (onOpenDrawer) {
                onOpenDrawer();
              } else {
                setActiveTab('scanner');
              }
            }}
            className="w-full px-3 py-2 rounded-md flex items-center gap-2.5 text-[13px] font-medium text-slate-300 hover:text-white hover:bg-[#141923] transition-colors text-left cursor-pointer"
          >
            <BarChart2 className="w-4 h-4 text-sky-400/70" />
            <span>深度診斷</span>
          </button>

          {/* Tab 5: ML工作室 */}
          <button
            type="button"
            data-testid="sidebar-studio-btn"
            onClick={() => setActiveTab('studio')}
            className={`w-full px-3 py-2 rounded-md flex items-center gap-2.5 text-[13px] font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'studio'
                ? 'bg-blue-950/60 text-terminal-cyan border-l-2 border-primary-container font-semibold shadow-sm'
                : 'text-slate-300 hover:text-white hover:bg-[#141923]'
            }`}
          >
            <Cpu className={`w-4 h-4 ${activeTab === 'studio' ? 'text-terminal-cyan' : 'text-sky-400/70'}`} />
            <span>ML工作室</span>
          </button>

          {/* Tab 6: 使用手冊全書 */}
          <button
            type="button"
            data-testid="sidebar-guide-btn"
            onClick={() => setActiveTab('guide')}
            className={`w-full px-3 py-2 rounded-md flex items-center gap-2.5 text-[13px] font-medium transition-colors text-left cursor-pointer ${
              activeTab === 'guide'
                ? 'bg-blue-950/60 text-terminal-cyan border-l-2 border-primary-container font-semibold shadow-sm'
                : 'text-slate-300 hover:text-white hover:bg-[#141923]'
            }`}
          >
            <BookOpen className={`w-4 h-4 ${activeTab === 'guide' ? 'text-terminal-cyan' : 'text-sky-400/70'}`} />
            <span>實戰手冊與回測</span>
          </button>
        </nav>

        {/* Quick Backtest Execution Trigger */}
        <div className="px-3 pt-3 space-y-2">
          <button
            type="button"
            data-testid="sidebar-backtest-btn"
            onClick={() => {
              if (onRunBacktest) {
                onRunBacktest();
              } else {
                setActiveTab('studio');
              }
            }}
            className="w-full py-2 px-3 rounded-md bg-primary-container hover:bg-blue-600 text-white text-xs font-mono font-semibold flex items-center justify-center gap-1.5 shadow-sm transition-all active:scale-98 cursor-pointer"
            title="立即執行全市場 1,826 檔 Walk-Forward 回測與模型重算"
          >
            <Play className="w-3.5 h-3.5 fill-white text-white" />
            <span>執行回測</span>
          </button>

          {onOpenCheatSheet && (
            <button
              type="button"
              data-testid="sidebar-cheatsheet-btn"
              onClick={onOpenCheatSheet}
              className="w-full py-1.5 px-3 rounded-md bg-[#131b29] hover:bg-[#1b2538] text-sky-300 border border-sky-500/30 text-xs font-mono font-medium flex items-center justify-center gap-1.5 transition-all cursor-pointer"
            >
              <BookOpen className="w-3.5 h-3.5 text-terminal-cyan" />
              <span>速查表</span>
            </button>
          )}
        </div>
      </div>

      {/* Dock Bottom Telemetry & Status */}
      <div className="flex flex-col gap-1.5 px-3.5 pt-3 text-[11px] font-mono text-slate-300">
        <div className="flex items-center gap-2 py-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terminal-cyan opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-terminal-cyan"></span>
          </span>
          <span className="text-terminal-cyan font-mono font-medium">連線遙測: 14ms</span>
        </div>
      </div>
    </aside>
  );
};
